<template>
  <div
    v-if="!isAccountsPage"
    ref="header"
    :class="`game-header--${currentRoute}`"
    class="game-header">
    <div class="game-header__body">
      <div
        ref="nav"
        class="game-header__nav">
        <router-link
          :to="navBackLink"
          class="nav-back">
          <img
            src="/images/icons/arrow-left.svg"
            alt="Arrow left"
            width="20px"
            height="10px">
          <span class="nav-back__title">{{ backButtonText }}</span>
        </router-link>

        <breadcrumbs
          :items="computedBreadcrumbs"
          class="game-header__breadcrumbs"
          @tagId="setActiveTag" />
      </div>

      <img
        v-if="currentRoute !== 'accounts'"
        ref="gameLogo"
        :src="headerInfo.image ? headerInfo.image : GameModel.getGameLogo(activeGame.id)"
        :data-fallback-img="headerInfo.fallbackImage || ''"
        alt=""
        height="80"
        width="80"
        class="game-header__logo"
        @error="_skGlobal.handleFallbackImg">

      <div
        ref="title"
        class="game-header__title-area">
        <h1 class="game-header__title">
          {{ headerTitle }}
          <span
            v-if="headerInfo.subtitle"
            class="game-header__subtitle">
            {{ headerInfo.subtitle }}
          </span>
        </h1>

        <span
          ref="reviews"
          class="game-header__reviews">
          <template v-if="headerInfo.reviews">
            <img
              src="/images/trustpilot-rate-5.svg"
              alt=""
              class="game-header__reviews-rating">
            <span class="game-header__reviews-count">{{ headerInfo.reviews }} reviews</span>
          </template>
        </span>
      </div>

      <video-about
        ref="video"
        class="game-header__video" />

      <p
        v-if="headerInfo.description && !accountOffer"
        class="game-header__description"
        v-html="headerInfo.description" />

      <div
        v-if="headerInfo.rating && !accountOffer"
        class="game-header__rating">
        <ui-star-rating
          :rating="+headerInfo.rating.rate"
          :orders-count="+headerInfo.rating.count" />
      </div>

      <offer-advantages
        v-if="headerInfo.advantages && !accountOffer"
        :list-model-key="headerInfo.advantages.key"
        :is-icon-compact="headerInfo.advantages.isIconCompact"
        class="game-header__advantages anti-content" />

      <ul
        v-if="relatedLinks.length"
        class="game-header__buttons scrollbar-hidden anti-content">
        <li
          v-for="{ related_title, related_url, title } in relatedLinks"
          :key="title"
          class="game-header__button">
          <router-link
            class="game-header__button-link transition--color-bg"
            :to="related_url">
            {{ related_title || title }}
          </router-link>
        </li>
      </ul>
    </div>
    <p
      v-if="headerInfo.tagDescription"
      class="game-header__tag-description">
      {{ headerInfo.tagDescription }}
    </p>
  </div>
</template>

<script>
import { mapGetters, mapState, mapMutations } from 'vuex';
import GameModel from 'Models/GameModel';
import Breadcrumbs from 'Components/ui/Breadcrumbs/Breadcrumbs.vue';
import VideoAbout from 'Components/VideoAbout/VideoAbout.vue';
import OfferAdvantages from 'Root/pages/game/components/OfferAdvantages.vue';
import UiStarRating from 'Components/ui/UiStarRating.vue';
import CurrencyOfferModel from 'Models/CurrencyOfferModel';

export default {
  name: 'GameHeader',

  components: {
    UiStarRating,
    OfferAdvantages,
    VideoAbout,
    Breadcrumbs,
  },

  data() {
    return {
      GameModel,
    };
  },

  computed: {
    ...mapState(['activeGame', 'userDevice']),
    ...mapState('products', ['headerInfo', 'product', 'accountOffer']),
    ...mapGetters('products', ['breadcrumbs']),

    currentRoute() {
      if (
        this.$route?.meta?.tagAsOfferInfo?.isAccounts
        || (
          this.product?.product_currency_setting_id
          && this.product?.product_currency_setting?.marketplace_type === CurrencyOfferModel.TYPES.ACCOUNT
        )
      ) {
        return 'accounts';
      }

      if (
        this.$route?.meta?.tagAsOfferInfo?.isGold
        || this.product?.meta?.servers
        || this.product?.product_currency_setting_id
      ) {
        return 'gold';
      }

      if (this.$route.name === 'game__product-detail' || this.$route.name.includes('game__tag-as-product')) {
        return 'detail';
      }

      if (this.$route.name === 'game__service') {
        return 'service';
      }

      if (this.$route.name === 'search') {
        return 'search';
      }

      return 'main';
    },

    navBackLink() {
      if (this.$route.meta.tagAsOfferInfo && !this.$route.meta.tagAsOfferInfo.isGold) {
        return `/${this.activeGame.routeNamespace}-boost`;
      }

      if (!this.breadcrumbs.length) {
        return '/';
      }

      return this.breadcrumbs[this.breadcrumbs.length - 2].href;
    },

    computedBreadcrumbs() {
      if (this.currentRoute === 'search') {
        const items = [
          {
            title: 'SkyCoach',
            href: '/',
          },
        ];

        if (this.$route.query.from === 'blog') {
          items.push({
            title: 'Blog',
            href: '/blog',
          });
        }

        items.push({
          title: 'Search Result',
          href: '/search',
        });

        return items;
      }

      return this.breadcrumbs;
    },

    backButtonText() {
      if (this.breadcrumbs.length >= 2 && !this.$route.meta.tagAsOfferInfo) {
        const prevBreadcrumb = this.breadcrumbs[this.breadcrumbs.length - 2];
        return prevBreadcrumb.title;
      }

      if (this.$route.meta.tagAsOfferInfo) {
        return this.activeGame.title;
      }

      return 'All Offers';
    },

    relatedLinks() {
      if (!this.product?.product_currency_setting?.related_product_currency_settings?.length) {
        return [];
      }

      return this.product.product_currency_setting.related_product_currency_settings
        .filter((setting) => setting.is_active);
    },

    headerTitle() {
      if (this.accountOffer) {
        return this.accountOffer.outer_name;
      }

      return this.headerInfo.title;
    },

    isAccountsPage() {
      if (
        (this.product?.product_currency_setting?.is_active || this.$route.query.offer_preview)
          && this.$route.params?.offerId
      ) {
        return false;
      }

      return !!(this.product?.product_currency_setting?.is_active
          && this.product.product_currency_setting?.marketplace_type === CurrencyOfferModel.TYPES.ACCOUNT);
    },
  },

  methods: {
    ...mapMutations('products', ['mutateActiveTag']),

    setActiveTag(id) {
      this.mutateActiveTag(id);
    },
  },
};
</script>

<style lang="scss" scoped>
$video-widget-width: 240px;
$video-widget-margin: 84px;

.game-header {
  position: relative;
  z-index: 2;
  line-height: 1.2;

  &.container {
    padding-top: 20px;
  }
}

.game-header__body {
  display: grid;
  grid-template-columns: auto auto;
  grid-template-rows: auto auto;
  align-items: center;
  position: relative;
  grid-gap: 4px 18px;
}

.game-header__title {
  font-weight: 500;
  font-size: 21px;
  display: block;
}

.game-header__tag-description {
  margin-top: 16px;
  font-size: 13px;
  line-height: 18px;
  font-weight: 400;
  color: $color--basic-white;
}

.game-header__subtitle {
  color: $color--basic-light-gray;
  margin-left: 8px;
}

.game-header__reviews {
  margin-top: 10px;
  display: inline-block;
  vertical-align: text-bottom;
}

.game-header__reviews-rating {
  height: 15px;
  display: inline-block;
  vertical-align: middle;
}

.game-header__reviews-count {
  display: inline-block;
  vertical-align: middle;
  margin-left: 8px;
  font-weight: 500;
  font-size: 11px;
}

.game-header__nav {
  width: 100%;
  display: flex;
  align-items: center;
}

.nav-back__title {
  margin-left: 15px;
}

.game-header__video {
  margin-top: 16px;
}

.game-header__description {
  font-size: 13px;
  font-weight: 400;
}

/deep/ .game-header__description {
  a {
    color: $color--basic-white;
    text-decoration: underline;
    font-weight: 500;
    transition: var(--transition-color);

    &:hover {
      color: $color--basic-light-gray;
    }
  }
}

.game-header__advantages {
  --item-background: transparent;
  --item-column-gap: 8px;
  --item-content-max-width: 110px;
}

.game-header__buttons {
  display: flex;
  align-items: center;
  overflow-x: auto;
}

.game-header__button {
  flex-shrink: 0;

  &:not(:first-child) {
    margin-left: 16px;
  }
}

.game-header__button-link {
  text-transform: uppercase;
  color: $color--basic-white;
  background-color: $color--basic-dark-gray;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  text-align: center;
  padding: 6px;
  display: inline-block;

  &:hover,
  &.router-link-active {
    background-color: $color--basic-blue;
  }
}

.game-header--accounts {
  .game-header__video,
  .nav-back,
  .game-header__reviews {
    display: none;
  }

  .game-header__body {
    grid-template-columns: 1fr 138px;
  }

  .game-header__nav {
    grid-column: 1 / -1;
  }

  .game-header__logo {
    grid-column: 2;
    grid-row: 1 / span 2;
    max-width: 120px;
    height: auto;
    min-width: 90px;
    width: 90%;
    justify-self: center;
  }

  .game-header__title-area {
    grid-column: 1 / -1;
    grid-row: 2;
  }

  .game-header__rating {
    grid-column: 2;
    grid-row: 3;
    align-self: start;
  }

  .game-header__body {
    grid-template-rows: auto;
    grid-row-gap: 16px;
  }

  .game-header__description {
    align-self: end;
    grid-row: 3;
  }

  .game-header__buttons {
    grid-row: 4;
    grid-column: 1 / 3;
  }
}

.game-header--gold {
  .game-header__nav,
  .game-header__video,
  .nav-back,
  .game-header__reviews {
    display: none;
  }

  .game-header__logo {
    grid-column: 2;
    grid-row: 1 / span 2;
    max-width: 120px;
    height: auto;
    min-width: 90px;
    width: 90%;
    justify-self: center;
  }

  .game-header__title-area {
    grid-column: 1;
    grid-row: 1;
  }

  .game-header__rating {
    grid-column: 1;
    grid-row: 2;
    align-self: start;
  }

  .game-header__body {
    grid-template-rows: min-content minmax(0, 1fr) auto auto;
    grid-row-gap: 16px;
  }

  .game-header__advantages,
  .game-header__description {
    grid-column: 1 / -1;
  }

  .game-header__buttons {
    grid-row: 4;
    grid-column: 1 / 3;
  }
}

.game-header--main {
  .game-header__description,
  .game-header__rating,
  .game-header__advantages,
  .game-header__nav,
  .game-header__logo,
  .nav-back,
  .game-header__reviews,
  .game-header__buttons {
    display: none;
  }
}

.game-header--service {
  .game-header__description,
  .game-header__rating,
  .game-header__advantages,
  .game-header__video,
  .game-header__logo,
  .game-header__nav,
  .game-header__reviews,
  .game-header__buttons,
  .nav-back {
    display: none;
  }
}

.game-header--search {
  .game-header__description,
  .game-header__rating,
  .game-header__advantages,
  .game-header__video,
  .game-header__logo,
  .game-header__nav,
  .game-header__reviews,
  .game-header__buttons,
  .nav-back {
    display: none;
  }
}

.game-header--detail {
  .game-header__description,
  .game-header__rating,
  .game-header__advantages,
  .game-header__video,
  .game-header__nav,
  .game-header__logo,
  .game-header__buttons {
    display: none;
  }
}

@media screen and #{$screen-less-than-640} {
  .game-header--accounts {
    /deep/ .ui-star-rating {
      position: relative;
      grid-template-columns: unset;
      padding-top: 28px;
    }

    /deep/ .ui-star-rating__wrapper {
      position: absolute;
      top: 0;
      right: 0;
    }
  }
}

@media screen and (min-width: 720px) {
  .game-header--detail {
    .game-header__title {
      font-weight: 500;
      font-size: 30px;
    }
  }
}

@media screen and (min-width: 450px) and #{$screen-less-than-1280} {
  .game-header--detail {
    .game-header__body {
      margin-right: 25%;
    }
  }
}

@media screen and (min-width: 720px) and #{$screen-less-than-1280} {
  .game-header--detail {
    min-height: 150px;
  }
}

@media screen and #{$screen-less-than-1024} {
  .game-header__body {
    grid-template-rows: auto;
  }
  .game-header__tag-description {
    margin-top: 24px;
    margin-bottom: 12px;
  }
  .game-header--main {
    .game-header__video {
      grid-row: 2;
    }
  }
}

@media screen and #{$screen-more-or-640} {
  .game-header--accounts {
    --game-header-logo-size: 0;

    &.container {
      padding: 24px var(--padding);
    }

    .game-header__body {
      position: relative;
      grid-template-rows: repeat(2, auto);
      grid-template-columns: 1fr 220px var(--game-header-logo-size);
      grid-row-gap: 24px;
    }

    .game-header__nav {
      display: flex;
      grid-column: 1;
    }

    .game-header__logo {
      display: none;
    }

    .game-header__title-area {
      grid-column: 1;
      grid-row: 2;
    }

    .game-header__description {
      grid-column: 1/ 3;
      font-size: 15px;
      font-weight: 400;
    }

    .game-header__rating {
      grid-column: 2;
      grid-row: 1 / 3;
      align-self: center;
    }

    .game-header__advantages {
      grid-column: 1 / 3;
      --column-gap: 40px;
    }
  }
}

@media screen and #{$screen-more-or-1024} {
  .game-header.container {
    padding-top: 40px;
  }

  .game-header__body {
    grid-template-columns: min-content auto #{$video-widget-width + $video-widget-margin};
  }

  .game-header__title {
    font-size: 25px;
    display: inline;
    margin-right: 16px;
  }

  .game-header__reviews-rating {
    height: auto;
  }

  .game-header__reviews-count {
    margin-left: 12px;
    font-weight: 500;
    font-size: 13px;
  }

  .game-header__logo {
    width: 80px;
    min-width: 80px; // min-content у грида сжимает до 0px
    height: auto;
  }

  .game-header__video {
    margin: 0 $video-widget-margin 0 0 !important; // chunks priority
    grid-row: 1 / -1;
    grid-column: 3;
  }

  .game-header--gold {
    --game-header-logo-size: #{$game-header-gold-logo-width-more-of-1024};

    .game-header__body {
      position: relative;
      grid-template-rows: repeat(4, auto);
      grid-template-columns: 1fr 220px var(--game-header-logo-size);
      grid-row-gap: 24px;
    }

    .game-header__nav {
      display: flex;
      grid-column: 1;
    }

    .game-header__logo {
      position: absolute;
      top: 0;
      right: 0;
      object-fit: contain;
      width: var(--game-header-logo-size);
      max-width: var(--game-header-logo-size);
      max-height: var(--game-header-logo-size);
    }

    .game-header__title-area {
      grid-column: 1;
      grid-row: 2;
    }

    .game-header__description {
      grid-column: 1/ 3;
    }

    .game-header__rating {
      grid-column: 2;
      grid-row: 1 / 3;
      align-self: center;
    }

    .game-header__advantages {
      grid-column: 1 / 3;
      --column-gap: 40px;
    }
  }

  .game-header--main {
    .game-header__logo {
      display: block;
    }

    .game-header__nav,
    .game-header__title-area {
      grid-column: 2;
      grid-row: 1;
    }

    .game-header__nav {
      display: flex;
      opacity: 0;
      visibility: hidden;
      pointer-events: none;
    }
  }

  .game-header--service {
    .game-header__nav {
      display: flex;
      grid-column: 2;
    }

    .game-header__video {
      display: grid;
    }

    .game-header__logo {
      display: block;
      grid-row: 1 / -1;
    }
  }

  .game-header--search {
    .game-header__nav {
      display: flex;
      grid-row: 1 / 2;
    }

    .game-header__title-area {
      grid-row: 2 / 3;
    }

    .game-header__nav,
    .game-header__title-area {
      grid-column: 1 / -1;
    }

    .game-header__video {
      display: grid;
    }

    .game-header__logo {
      display: none;
    }

    .game-header__title {
      font-size: 35px;
    }

    .game-header__subtitle {
      font-size: 25px;
    }
  }

  .game-header--detail {
    .game-header__body {
      gap: 20px 18px;
    }

    .game-header__nav {
      display: flex;
      grid-column: 1 / -1;
    }

    .game-header__logo {
      display: block;
      width: 50px;
      min-width: 50px; // min-content у грида сжимает до 0px
      grid-row: 2;
    }
  }

  .game-header .nav-back {
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-weight: 500;
    font-size: 13px;
    color: $color--basic-white;
    background-color: $color--basic-black;
    padding: 7px 9px;
    border-radius: 5px;
    margin-right: 19px;
    pointer-events: auto;
    opacity: 1;
    visibility: visible;
    flex-shrink: 0;
  }

  .game-header--main .nav-back,
  .game-header--accounts .nav-back,
  .game-header--gold .nav-back,
  .game-header--service .nav-back,
  .game-header--search .nav-back {
    display: none;
    visibility: hidden;
    opacity: 0;
    pointer-events: none;
    transform: translateX(-100%) translateX(-19px);
  }

  .game-header__breadcrumbs {
    flex-shrink: 0;
    max-width: 100%; // а это чтобы не вылезало за край контейнера
  }

  .game-header--detail .game-header__breadcrumbs {
    transform: translateX(0);
  }

  .game-header__advantages {
    --item-column-gap: 12px;
  }
}

@media screen and #{$screen-more-or-1280} {
  .game-header__reviews {
    margin-top: 0;
  }

  .game-header--gold {
    --game-header-logo-size: #{$game-header-gold-logo-width-more-of-1280};

    .game-header__description {
      font-size: 15px;
    }
  }
}

@media screen and #{$screen-more-or-1366} {
  .game-header__title {
    font-size: 35px;
  }
}
</style>
